// =============================================================================
// Notification client types + query keys (Portal)
// =============================================================================
// Shared shapes for the in-app inbox, banners, and preferences surfaces. All
// endpoints are scoped server-side to the authenticated user.
// =============================================================================

export interface InboxNotification {
  id: string;
  type: string;
  category: string;
  title: string;
  body: string | null;
  /**
   * Sanitized rich message (server-side strict allow-list). Present for
   * announcement rows so links render clickable; null for system notifications,
   * where `body` (plain text) is shown instead.
   */
  bodyHtml: string | null;
  referenceType: string | null;
  referenceId: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface InboxResponse {
  items: InboxNotification[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    unread: number;
  };
}

export interface UnreadCountResponse {
  count: number;
}

export interface Banner {
  id: string;
  title: string;
  bodyHtml: string;
  tone: string;
  createdAt: string;
}

export interface NotificationPreference {
  type: string;
  emailEnabled: boolean;
  portalEnabled: boolean;
}

/** How often the bell polls the unread count (ms). */
export const UNREAD_POLL_INTERVAL_MS = 60_000;

export const notificationKeys = {
  unreadCount: ['notifications', 'unread-count'] as const,
  inbox: (page: number, unreadOnly: boolean) =>
    ['notifications', 'inbox', { page, unreadOnly }] as const,
  recent: ['notifications', 'recent'] as const,
  banners: ['notifications', 'banners'] as const,
  preferences: ['notifications', 'preferences'] as const,
};

/** Human label for a NotificationType used in the preferences UI. */
export const PREFERENCE_LABELS: Record<string, string> = {
  ORDER_RECEIVED: 'Order received',
  ORDER_IN_PRODUCTION: 'Order in production',
  ORDER_SHIPPED: 'Order shipped',
  ORDER_DELIVERED: 'Order delivered',
  ORDER_ON_HOLD: 'Order on hold',
  ORDER_AWAITING_APPROVAL: 'Order awaiting approval',
  ORDER_CANCELLED: 'Order cancelled',
  PAYMENT_RECEIVED: 'Payment received',
  PAYMENT_FAILED: 'Payment failed',
  LOW_BALANCE: 'Low wallet balance',
  INVOICE_CREATED: 'Invoice created',
};

/** Relative "time ago" formatting for inbox rows. */
export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Map a notification's reference to a Portal deep link (or null). */
export function notificationHref(n: InboxNotification): string | null {
  if (n.referenceType === 'order' && n.referenceId) {
    return `/orders/${n.referenceId}`;
  }
  if (n.type === 'LOW_BALANCE' || n.type === 'PAYMENT_FAILED') {
    return '/billing?tab=wallet';
  }
  return null;
}
